import { NavLink, Outlet, useLocation } from "react-router-dom";
import {
  LayoutDashboard, TrendingUp, Wallet, Target,
  Building2, Box, Banknote, Bell, Lock, Shield,
  LogOut, ChevronRight, X, Calendar, ChevronDown, Menu,
  PanelLeftClose, PanelLeftOpen, Zap, Settings, Sparkles
} from "lucide-react";
import { useState, useEffect, useRef, createContext, useContext } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const NEXUS_LOGO = "/logo.png";
const TimeContext = createContext(null);

const PERIODS = [
  { key: "mtd", label: "MTD" },
  { key: "qtd", label: "QTD" },
  { key: "ytd", label: "YTD" },
  { key: "as_on_date", label: "As On Date" },
];

const navItems = [
  { name: "Executive Command", icon: LayoutDashboard, path: "/dashboard" },
  { name: "Sales Intelligence", icon: TrendingUp, path: "/dashboard/sales" },
  { name: "Collections", icon: Wallet, path: "/dashboard/collections" },
  { name: "Target vs Achievement", icon: Target, path: "/dashboard/targets" },
  { name: "Project Intelligence", icon: Building2, path: "/dashboard/projects" },
  { name: "Inventory", icon: Box, path: "/dashboard/inventory" },
  { name: "Blocked Units", icon: Lock, path: "/dashboard/blocked" },
  { name: "Engineering Milestone", icon: Banknote, path: "/dashboard/cashflow" },
  { name: "Strategic Insights", icon: Sparkles, path: "/dashboard/strategic" },
  { name: "Nexus AI", icon: Sparkles, path: "/dashboard/ai-assistant" },
  { name: "Settings", icon: Settings, path: "/dashboard/settings" },
];

const mobileBottomTabs = [
  { name: "Home", icon: LayoutDashboard, path: "/dashboard" },
  { name: "Sales", icon: TrendingUp, path: "/dashboard/sales" },
  { name: "Nexus AI", icon: Sparkles, path: "/dashboard/ai-assistant" },
  { name: "Targets", icon: Target, path: "/dashboard/targets" },
  { name: "Collect", icon: Wallet, path: "/dashboard/collections" },
];

function getBreadcrumb(pathname) {
  const item = navItems.find(n => n.path === pathname);
  return item ? item.name : "Dashboard";
}

export function useTimeContext() {
  return useContext(TimeContext);
}

export default function DashboardLayout({ username, onLogout, token }) {
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const currentPage = getBreadcrumb(location.pathname);

  const [period, setPeriod] = useState("ytd");
  const [months, setMonths] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [monthsLoading, setMonthsLoading] = useState(true);
  const [monthDropdownOpen, setMonthDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    let retryCount = 0;
    const maxRetries = 3;
    const fetchMonths = async () => {
      setMonthsLoading(true);
      try {
        const res = await axios.get(`${API}/kpi/available-months`, { headers: { Authorization: `Bearer ${token}`, 'Cache-Control': 'no-cache' }, params: { _t: Date.now() } });
        const monthsList = res.data.months || [];
        setMonths(monthsList);
        if (res.data.latest) {
          setSelectedMonth(res.data.latest);
        } else if (monthsList.length > 0) {
          setSelectedMonth(monthsList[0].value);
        }
      } catch (err) {
        console.error('Failed to fetch months:', err);
        if (retryCount < maxRetries) {
          retryCount++;
          setTimeout(fetchMonths, 2000 * retryCount);
          return;
        }
      }
      setMonthsLoading(false);
    };
    fetchMonths();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- API/axios are module constants, setters are stable
  }, [token]);

  const refDate = selectedMonth ? (() => {
    const [y, m] = selectedMonth.split("-").map(Number);
    const lastDay = new Date(y, m, 0).getDate();
    return `${y}-${String(m).padStart(2, "0")}-${String(lastDay).padStart(2, "0")}`;
  })() : null;

  const currentMonthInfo = months.find(m => m.value === selectedMonth);

  useEffect(() => {
    const handler = (e) => { if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setMonthDropdownOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const timeContext = { period, setPeriod, selectedMonth, setSelectedMonth, refDate, months, currentMonthInfo, token };
  const sidebarWidth = sidebarCollapsed ? 72 : 260;

  const hideTimeControls = ['/dashboard/settings', '/dashboard/inventory', '/dashboard/ai-assistant'].includes(location.pathname);

  return (
    <TimeContext.Provider value={timeContext}>
      <div className="flex min-h-screen bg-[#F8FAFC] max-w-[100vw] overflow-x-hidden" data-testid="dashboard-layout">
        {/* Overlay */}
        {sidebarOpen && <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-30" onClick={() => setSidebarOpen(false)} />}

        {/* ===== SIDEBAR ===== */}
        <aside className={`sidebar ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0`} style={{ width: `${sidebarWidth}px` }} data-testid="sidebar">
          <div className={`${sidebarCollapsed ? 'px-3' : 'px-5'} pt-5 pb-4 border-b border-white/[0.04]`}>
            <div className="flex items-center justify-between">
              <div className={`flex items-center ${sidebarCollapsed ? 'justify-center w-full' : 'gap-2.5'}`}>
                <img src={NEXUS_LOGO} alt="Nexus" className={`${sidebarCollapsed ? 'h-9 w-9' : 'h-12 w-12'} object-contain`} data-testid="sidebar-logo" />
                {!sidebarCollapsed && <span className="text-lg font-bold text-white tracking-tight" style={{ fontFamily: 'Manrope' }}>Nexus</span>}
              </div>
              <button className="lg:hidden p-1 hover:bg-white/10 rounded text-slate-400" onClick={() => setSidebarOpen(false)}><X className="h-5 w-5" /></button>
            </div>
            {/* User profile + logout in mobile sidebar */}
            <div className="lg:hidden mt-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-full bg-emerald-600 flex items-center justify-center text-white text-base font-bold">{username?.charAt(0)?.toUpperCase() || 'A'}</div>
                <div><p className="text-sm font-semibold text-white">{username}</p><p className="text-[10px] text-slate-400">Administrator</p></div>
              </div>
              <button onClick={onLogout} className="flex items-center gap-1.5 px-3 py-1.5 bg-white/10 hover:bg-red-500/20 rounded-lg text-slate-300 hover:text-red-400 transition-colors" data-testid="mobile-logout-header">
                <LogOut className="h-3.5 w-3.5" /><span className="text-xs">Logout</span>
              </button>
            </div>
            {!sidebarCollapsed && <p className="text-[9px] font-semibold text-blue-400/50 uppercase tracking-[0.2em] mt-3 hidden lg:block">Intelligence Platform</p>}
          </div>

          <nav className="flex-1 py-4 overflow-y-auto" data-testid="sidebar-nav">
            {!sidebarCollapsed && <div className="px-5 mb-3 hidden lg:block"><span className="text-[9px] font-bold text-slate-500/60 uppercase tracking-[0.15em]">Modules</span></div>}
            {navItems.map((item) => (
              <NavLink key={item.path} to={item.path} end={item.path === "/dashboard"}
                className={({ isActive }) => `sidebar-nav-item ${isActive ? 'active' : ''} ${sidebarCollapsed ? '!justify-center !px-0 !mx-2' : ''}`}
                data-testid={`nav-${item.path.split('/').pop() || 'dashboard'}`}
                onClick={() => setSidebarOpen(false)}
                title={sidebarCollapsed ? item.name : undefined}>
                <item.icon className="h-[16px] w-[16px] flex-shrink-0" />
                {!sidebarCollapsed && <span>{item.name}</span>}
              </NavLink>
            ))}
          </nav>

          {/* Collapse button — desktop */}
          <div className="hidden lg:block border-t border-white/[0.04]">
            <button onClick={() => setSidebarCollapsed(!sidebarCollapsed)} className="w-full flex items-center justify-center py-3 text-slate-500 hover:text-white hover:bg-white/5 transition-colors" data-testid="sidebar-collapse-btn">
              {sidebarCollapsed ? <PanelLeftOpen className="h-4 w-4" /> : <PanelLeftClose className="h-4 w-4" />}
            </button>
          </div>

          {/* User — desktop */}
          {!sidebarCollapsed && (
            <div className="p-4 border-t border-white/[0.04] hidden lg:block">
              <div className="flex items-center gap-3">
                <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white text-sm font-semibold">{username?.charAt(0)?.toUpperCase() || 'A'}</div>
                <div className="flex-1 min-w-0"><p className="text-sm font-medium text-white/90 truncate">{username}</p><p className="text-[10px] text-slate-500">Administrator</p></div>
                <button onClick={onLogout} className="p-1.5 hover:bg-red-500/10 rounded-md text-slate-500 hover:text-red-400 transition-colors" data-testid="logout-button"><LogOut className="h-4 w-4" /></button>
              </div>
            </div>
          )}
          {sidebarCollapsed && (
            <div className="p-2 border-t border-white/[0.04] hidden lg:flex justify-center">
              <button onClick={onLogout} className="p-2 hover:bg-red-500/10 rounded-md text-slate-500 hover:text-red-400 transition-colors"><LogOut className="h-4 w-4" /></button>
            </div>
          )}
        </aside>

        {/* ===== MAIN ===== */}
        <div className="main-content flex-1 flex flex-col min-w-0" style={{ '--sidebar-w': `${sidebarWidth}px` }}>
          {/* Header */}
          <header className="app-header" data-testid="app-header">
            {/* Row 1: Nav + Month */}
            <div className="flex items-center gap-2 sm:gap-3 min-w-0">
              <button className="lg:hidden p-1.5 sm:p-2 hover:bg-slate-100 rounded-lg flex-shrink-0" onClick={() => setSidebarOpen(true)} data-testid="mobile-menu-button">
                <Menu className="h-5 w-5 text-slate-600" />
              </button>
              <div className="flex items-center gap-1.5 lg:hidden flex-shrink-0">
                <img src={NEXUS_LOGO} alt="Nexus" className="h-10 w-10 object-contain" />
                <span className="text-base font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Manrope' }}>Nexus</span>
              </div>
              <div className="flex items-center text-sm text-slate-500 min-w-0 hidden lg:flex">
                <span className="font-medium text-slate-400">Nexus</span>
                <ChevronRight className="h-3.5 w-3.5 mx-1.5 text-slate-300" />
                <span className="font-semibold text-slate-800 truncate">{currentPage}</span>
              </div>
            </div>
            <div className="flex items-center gap-1.5 sm:gap-2.5 flex-shrink-0">
              {!hideTimeControls && currentMonthInfo && (
                <div className="hidden md:flex items-center gap-1.5 text-[10px] mr-1" data-testid="fy-context-label">
                  <span className="font-semibold text-blue-600">{currentMonthInfo.fy_label}</span>
                  <span className="text-slate-300">|</span>
                  <span className="font-semibold text-indigo-600">{currentMonthInfo.quarter_label}</span>
                </div>
              )}
              {!hideTimeControls && (
              <div className="relative" ref={dropdownRef}>
                <button onClick={() => setMonthDropdownOpen(!monthDropdownOpen)} className="flex items-center gap-1.5 px-2.5 py-1.5 bg-white border border-slate-200 rounded-lg hover:border-slate-300 transition-all text-xs font-medium text-slate-700 shadow-sm" data-testid="month-selector-trigger">
                  <Calendar className="h-3.5 w-3.5 text-slate-400" />
                  <span>{monthsLoading ? "Loading..." : currentMonthInfo ? currentMonthInfo.label : months.length > 0 ? months[0].label : "Select Month"}</span>
                  <ChevronDown className={`h-3 w-3 text-slate-400 transition-transform ${monthDropdownOpen ? "rotate-180" : ""}`} />
                </button>
                {monthDropdownOpen && (
                  <div className="absolute right-0 top-full mt-1 w-52 max-h-60 overflow-y-auto bg-white border border-slate-200 rounded-xl shadow-xl z-50" data-testid="month-selector-dropdown">
                    {months.map((m) => (
                      <button key={m.value} onClick={() => { setSelectedMonth(m.value); setMonthDropdownOpen(false); }} className={`w-full flex items-center justify-between px-3 py-2 text-xs hover:bg-slate-50 transition-colors ${selectedMonth === m.value ? "bg-blue-50 text-blue-700 font-semibold" : "text-slate-700"}`} data-testid={`month-option-${m.value}`}>
                        <span>{m.label}</span><span className="text-[10px] text-slate-400 font-normal">{m.quarter_label}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              )}
              {!hideTimeControls && (
              <div className="hidden sm:flex items-center gap-0.5 bg-slate-100 rounded-lg p-0.5" data-testid="period-selector">
                {PERIODS.map(p => (
                  <button key={p.key} onClick={() => setPeriod(p.key)} className={`px-2.5 py-1.5 text-[11px] font-semibold rounded-md transition-all whitespace-nowrap ${period === p.key ? 'bg-[#1D4ED8] text-white shadow-sm' : 'text-slate-500 hover:text-slate-700'}`} data-testid={`period-${p.key}`}>
                    {p.key === 'as_on_date' ? 'As On Date' : p.label}
                  </button>
                ))}
              </div>
              )}
            </div>
          </header>

          {/* Mobile period selector — full width row below header */}
          {!hideTimeControls && (
          <div className="sm:hidden flex items-center justify-center gap-1 bg-white border-b border-slate-200 px-3 py-2" data-testid="mobile-period-bar">
            {PERIODS.map(p => (
              <button key={p.key} onClick={() => setPeriod(p.key)} className={`flex-1 py-2 text-xs font-bold rounded-lg transition-all text-center ${period === p.key ? 'bg-[#1D4ED8] text-white shadow-sm' : 'text-slate-500 bg-slate-50 hover:bg-slate-100'}`} data-testid={`m-period-${p.key}`}>
                {p.key === 'as_on_date' ? 'AOD' : p.label}
              </button>
            ))}
          </div>
          )}

          <main className="page-content flex-1 fade-in">
            <Outlet context={timeContext} />
          </main>
        </div>

        {/* ===== MOBILE BOTTOM NAV ===== */}
        <nav className="mobile-bottom-nav" data-testid="mobile-bottom-nav">
          <div className="mobile-bottom-nav-inner">
            {mobileBottomTabs.map((tab) => {
              const isActive = tab.path === "/dashboard" ? location.pathname === "/dashboard" : location.pathname.startsWith(tab.path);
              return (
                <NavLink key={tab.name} to={tab.path} end={tab.path === "/dashboard"} className={`mobile-nav-tab ${isActive ? 'active' : ''}`} data-testid={`mobile-nav-${tab.name.toLowerCase()}`}>
                  <div className="mobile-nav-icon"><tab.icon className="h-[18px] w-[18px]" /></div>
                  <span>{tab.name}</span>
                </NavLink>
              );
            })}
          </div>
        </nav>
      </div>
    </TimeContext.Provider>
  );
}
